import { CompanyResponseDto, NumericReleaseResponseDto, OrderResponseDto } from '@planning/app/api'
import { EventTypes } from '@planning/messages/eventsTypes'
import { GetNumericReleaseByIdQuery } from '@planning/messages/queries/getNumericReleaseByIdQueryHandler'
import { GetNumericReleasesQuery } from '@planning/messages/queries/getNumericReleasesQueryHandler'
import { IEvent, IMessageBus } from '@tom-ui/utils'
import _ from 'lodash'
import { action, computed, makeObservable } from 'mobx'
import { ItemStore } from '../base/ItemStore'
import { CompanyItemStore } from '../externalDriver/CompanyItemStore'
import { OrderItemStore } from '../order/OrderItemStore'
import { RailVisitItemStore } from '../railVisit/RailVisitItemStore'
import { VesselVisitItemStore } from '../vesselVisit/VesselVisitItemStore'
import { INumericReleaseItem, NumericReleaseItem } from './NumericReleaseItem'

export class NumericReleaseItemStore extends ItemStore<
  NumericReleaseResponseDto,
  INumericReleaseItem
> {
  constructor(
    private readonly messageBus: IMessageBus,
    private readonly orderItemStore?: OrderItemStore,
    private readonly companyItemStore?: CompanyItemStore,
    private readonly vesselVisitItemStore?: VesselVisitItemStore,
    private readonly railVisitItemStore?: RailVisitItemStore,
  ) {
    super(
      (key, data) =>
        new NumericReleaseItem(
          key,
          data,
          this.orderItemStore,
          this.companyItemStore,
          this.vesselVisitItemStore,
          this.railVisitItemStore,
        ),
      {
        messageBus,
        fetchFunc: (id: number) => new GetNumericReleaseByIdQuery(id),
      },
    )
    makeObservable(this, {
      receiveNumericReleasesMessage: action,
      receiveNumericReleaseMessage: action,
      receiveNumericReleaseDeletedEventMessage: action,

      items: computed,
    })

    messageBus.subscribeEvent(GetNumericReleasesQuery.type, this.receiveNumericReleasesMessage)
    messageBus.subscribeEvent(GetNumericReleaseByIdQuery.type, this.receiveNumericReleaseMessage)
    messageBus.subscribeEvent(
      EventTypes.NumericReleaseUpsertedEvent,
      this.receiveNumericReleaseMessage,
    )

    messageBus.subscribeEvent(
      EventTypes.NumericReleasesDeletedEvent,
      this.receiveNumericReleaseDeletedEventMessage,
    )

    messageBus.subscribeEvent(
      EventTypes.CompanyUpsertedInternalEvent,
      this.receiveCompanyUpsertedEventMessage,
    )

    messageBus.subscribeEvent(
      EventTypes.OrdersUpsertedEvent,
      this.receiveOrdersUpsertedEventMessage,
    )
  }

  //TODO: this computed prop could be a mapping to a common DTO for both release types
  get items(): INumericReleaseItem[] {
    return _(this.elements)
      .map(x => ({
        ...x,
        carrierName: x.carrierName,
        operator: x.operator,
        orders: x.orders,
        assigned: x.assigned,
        fulfilled: x.fulfilled,
        canDelete: x.canDelete,
        canEdit: x.canEdit,
        isExpired: x.isExpired,
      }))
      .value()
  }

  fetchAll = async () => {
    if (this.hasBeenInitialized) return

    this.hasBeenInitialized = true
    return this.messageBus.dispatchQuery(new GetNumericReleasesQuery())
  }

  receiveNumericReleasesMessage = (event: IEvent<NumericReleaseResponseDto[]>) => {
    if (event.payload) this.upsertBulk(event.payload)
  }

  receiveNumericReleaseMessage = (event: IEvent<NumericReleaseResponseDto>) => {
    console.log('Received Numeric Release Message', event.payload)
    if (event.payload) this.upsert(event.payload)
  }

  receiveNumericReleaseDeletedEventMessage = (event: IEvent<number>): void => {
    if (event.payload && _.has(this.elements, event.payload)) this.delete(event.payload)
  }

  receiveCompanyUpsertedEventMessage = (event: IEvent<CompanyResponseDto>): void => {
    if (event.payload) {
      const itemsToUpdate = Object.values(this.elements)
        .filter(item => item.data.shippingLineId === event.payload.id)
        .map(item => {
          return {
            ...item.data,
            shippingLineName: event.payload.name,
            shippingLineShortName: event.payload.shortName,
          }
        })

      this.upsertBulk(itemsToUpdate)
    }
  }

  receiveOrdersUpsertedEventMessage = async (event: IEvent<OrderResponseDto[]>): Promise<void> => {
    if (!event.payload) return

    const itemsToUpdate: NumericReleaseResponseDto[] = []

    for (const order of event.payload) {
      const originRelease = Object.values(this.elements).find(nr =>
        nr.data.assignedOrders?.some(o => o.id === order.id),
      )

      const targetRelease = order.numericReleaseId
        ? this.elements[order.numericReleaseId]
        : undefined

      if (originRelease) {
        const { data } = originRelease

        const isSameRelease = originRelease.id === order.numericReleaseId

        if (isSameRelease) {
          data.assignedOrders?.forEach(o => {
            if (o.id === order.id) o.status = order.status
          })
        } else {
          data.assignedOrders = data.assignedOrders?.filter(o => o.id !== order.id) ?? []
        }

        itemsToUpdate.push(data)
      }

      if (targetRelease) {
        const { data } = targetRelease

        data.assignedOrders = [...(data.assignedOrders ?? [])]

        if (!data.assignedOrders.some(o => o.id === order.id)) {
          data.assignedOrders.push({
            id: order.id,
            status: order.status,
          })
        }

        itemsToUpdate.push(data)
      }
    }

    this.upsertBulk(itemsToUpdate)
  }
}
